import React, { useState } from 'react';
import { rooms } from '../../data/mock';
import { SectionEyebrow, SectionTitle, SectionLead, Reveal } from './UI';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { IMAGE_SIZES, responsiveImageProps } from '../../lib/images';

const StaySection = ({ onBook }) => {
  const [i, setI] = useState(0);
  const room = rooms[i];
  const prev = () => setI((v) => (v - 1 + rooms.length) % rooms.length);
  const next = () => setI((v) => (v + 1) % rooms.length);

  return (
    <section id="stay" className="bg-white">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-24 md:pt-32">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 items-end">
          <div className="md:col-span-6">
            <Reveal>
              <SectionEyebrow>Stay</SectionEyebrow>
              <SectionTitle className="mt-6">Rooms & Suites,<br /> written by the water.</SectionTitle>
            </Reveal>
          </div>
          <div className="md:col-span-5 md:col-start-8">
            <Reveal>
              <SectionLead>
                129 rooms and suites, all facing gardens or the Bosphorus — furnished with hand-woven textiles, natural stone and objects made by Turkish craftspeople.
              </SectionLead>
            </Reveal>
          </div>
        </div>

        {/* Tab nav */}
        <div className="mt-10 md:mt-14 border-t border-neutral-200">
          <div className="flex gap-6 md:gap-10 overflow-x-auto no-scrollbar">
            {rooms.map((r, idx) => (
              <button
                key={r.id}
                onClick={() => setI(idx)}
                className={`shrink-0 py-5 tracking-[0.22em] text-[11px] uppercase border-t-2 -mt-px transition-colors ${
                  i === idx ? 'border-neutral-900 text-neutral-900' : 'border-transparent text-neutral-500 hover:text-neutral-900'
                }`}
              >
                {r.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main visual + details */}
      <div className="mt-8 md:mt-12">
        <div className="grid md:grid-cols-12 gap-0 items-stretch">
          <div className="md:col-span-8 relative overflow-hidden">
            <div className="relative w-full h-[52vh] md:h-[78vh]">
              <img
                key={room.id}
                {...responsiveImageProps(room.image, IMAGE_SIZES.roomHero)}
                alt={room.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-opacity duration-700"
              />
              <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6 flex gap-2">
                <button onClick={prev} aria-label="Previous room" className="w-10 h-10 md:w-11 md:h-11 bg-white/85 hover:bg-white text-neutral-900 flex items-center justify-center transition-colors">
                  <ChevronLeft className="w-4 h-4" strokeWidth={1.25} />
                </button>
                <button onClick={next} aria-label="Next room" className="w-10 h-10 md:w-11 md:h-11 bg-white/85 hover:bg-white text-neutral-900 flex items-center justify-center transition-colors">
                  <ChevronRight className="w-4 h-4" strokeWidth={1.25} />
                </button>
              </div>
              <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 text-white/90 tracking-[0.22em] text-[10.5px]">
                {String(i + 1).padStart(2, '0')} / {String(rooms.length).padStart(2, '0')}
              </div>
            </div>
          </div>
          <div className="md:col-span-4 bg-[#f6f3ee] p-8 md:p-12 flex flex-col justify-between">
            <div>
              <div className="tracking-[0.28em] text-[10.5px] uppercase text-neutral-500">Signature Room</div>
              <h3 className="mt-4 font-serif text-3xl md:text-4xl leading-tight text-neutral-900">{room.name}</h3>
              <p className="mt-5 text-neutral-600 font-light leading-relaxed">{room.desc}</p>
              <dl className="mt-8 space-y-3 text-[13px]">
                <div className="flex justify-between border-b border-neutral-200 pb-2">
                  <dt className="tracking-[0.22em] text-[10.5px] uppercase text-neutral-500">Size</dt>
                  <dd className="text-neutral-900">{room.size}</dd>
                </div>
                <div className="flex justify-between border-b border-neutral-200 pb-2">
                  <dt className="tracking-[0.22em] text-[10.5px] uppercase text-neutral-500">View</dt>
                  <dd className="text-neutral-900">{room.view}</dd>
                </div>
                <div className="flex justify-between border-b border-neutral-200 pb-2">
                  <dt className="tracking-[0.22em] text-[10.5px] uppercase text-neutral-500">Beds</dt>
                  <dd className="text-neutral-900">{room.beds}</dd>
                </div>
              </dl>
              <ul className="mt-6 grid grid-cols-2 gap-y-2 text-[13px] text-neutral-700">
                {room.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-2">
                    <span className="mt-[7px] w-1 h-1 bg-neutral-900 shrink-0" />
                    {h}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-10 flex items-center justify-between gap-4">
              <div>
                <div className="tracking-[0.22em] text-[10.5px] uppercase text-neutral-500">Rate</div>
                <div className="mt-1 font-serif text-xl text-neutral-900">{room.price}</div>
              </div>
              <button
                onClick={onBook}
                className="px-6 h-11 bg-neutral-900 text-white tracking-[0.22em] text-[11px] uppercase hover:bg-neutral-800 transition-colors"
              >
                Book Now
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaySection;
